// src/pages/LoginPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { loginUser, loginWithGoogle } from "@/services/authService";

const ROOT_LOGO =
  "https://storage.googleapis.com/msgsndr/ygqUk4JVwxGzD160zhvF/media/696b46c8e125ef6ab4b0d738.png";
const ROOT_BRAND_COLOR = "#4aa936";

const safeSessionGet = (key: string) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const LoginPage: React.FC = () => {
  const { firmSettings, firmId } = useAuth();
  const [email, setEmail] = useState("");
  const [locationKey, setLocationKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [bootingSso, setBootingSso] = useState(false);
  const [error, setError] = useState("");

  const isBrandedPortal = !!firmId && firmSettings?.name !== "Filers Hub";

  const displayLogo = isBrandedPortal ? firmSettings?.logoUrl || ROOT_LOGO : ROOT_LOGO;
  const displayName = isBrandedPortal ? firmSettings?.name || "Filers Hub" : "Filers Hub";
  const brandColor = isBrandedPortal ? firmSettings?.brandColor || ROOT_BRAND_COLOR : ROOT_BRAND_COLOR;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    const cachedEmail = safeSessionGet("FILERSHUB_SSO_EMAIL");
    const cachedLoc = safeSessionGet("FILERSHUB_SSO_LOC");

    const emailParam = urlParams.get("userEmail") || urlParams.get("email") || cachedEmail;
    const locParam = urlParams.get("locationId") || urlParams.get("locationKey") || cachedLoc;

    const cleanedEmail = (emailParam || "").trim();
    const cleanedLoc = (locParam || "").trim();

    if (cleanedEmail && !cleanedEmail.includes("{{")) setEmail(cleanedEmail);
    if (cleanedLoc) setLocationKey(cleanedLoc);

    // If we have both params, auto-run SSO so the iframe never sits at the form.
    if (cleanedEmail && cleanedLoc && !cleanedEmail.includes("{{")) {
      setBootingSso(true);
      setLoading(true);
      setError("");

      loginUser(cleanedEmail, cleanedLoc)
        .catch((err: any) => {
          setError(err?.message || "SSO authentication failed.");
          setLoading(false);
          setBootingSso(false);
        });
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !locationKey) {
      setError("Email and Authorized Key are required.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await loginUser(email, locationKey);
    } catch (err: any) {
      setError(err?.message || "Authentication failed. Check your authorized key.");
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError("");
    try {
      await loginWithGoogle();
    } catch (err: any) {
      setError(err?.message || "Provider authentication failed.");
      setLoading(false);
    }
  };

  const navigateToLegal = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    e.preventDefault();
    window.history.pushState({}, "", path);
    window.dispatchEvent(new Event("filershub:navigate"));
  };

  if (bootingSso) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
            <div>
              <h1 className="text-lg font-black text-slate-900">Signing you in…</h1>
              <p className="text-sm text-slate-500 mt-1">Establishing a secure session inside GoHighLevel.</p>
              {error ? <p className="text-xs text-red-600 font-bold mt-3">{error}</p> : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Your existing JSX below (unchanged), it will only render when not auto-SSOing.
  return (
    <div className="min-h-screen bg-[#f3f6f9] flex flex-col items-center justify-center px-6 py-12 font-sans relative overflow-hidden">
      {/* ... keep the rest of your component exactly as-is ... */}
      {/* Only change is the safe session + auto SSO boot above */}
      <div className="w-full max-w-[440px] animate-fade-in-up z-10">
        {/* existing content */}
        {/* Your form and buttons remain the same */}
      </div>

      <div className="mt-10 text-center space-y-4">
        <p className="text-[9px] font-black text-slate-300 uppercase tracking-[0.4em]">Filers Hub Infrastructure</p>
        <div className="flex items-center justify-center gap-6 text-[10px] font-bold text-slate-400">
          <a href="/privacy" onClick={(e) => navigateToLegal(e, "/privacy")} className="hover:text-brand-600 transition-colors">
            Privacy Policy
          </a>
          <span className="text-slate-200">•</span>
          <a href="/terms" onClick={(e) => navigateToLegal(e, "/terms")} className="hover:text-brand-600 transition-colors">
            Terms of Service
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
